import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { listOrdersForFarmer } from "@/lib/api";
import { statusColor } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, ArrowRight, Truck, CheckCircle2, Clock, RotateCcw, FileText } from "lucide-react";
import { toast } from "sonner";
import LoginDialog from "@/components/LoginDialog";

const statusIcons = {
  placed: Clock,
  packed: Package,
  out_for_delivery: Truck,
  delivered: CheckCircle2,
  cancelled: Clock,
};

const OrdersPage = () => {
  const { t, lang, farmer, reorderItems } = useApp();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(!farmer);
  const navigate = useNavigate();

  useEffect(() => {
    if (!farmer) {
      setLoading(false);
      return;
    }
    listOrdersForFarmer(farmer.id)
      .then(setOrders)
      .finally(() => setLoading(false));
  }, [farmer]);

  const handleReorder = (order) => {
    reorderItems(order.items);
    toast.success(t("reorderConfirm"));
    navigate("/cart");
  };

  if (!farmer) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <Package className="w-16 h-16 mx-auto text-slate-300 mb-3" />
        <h2 className="font-heading font-bold text-2xl mb-2">{t("loginTitle")}</h2>
        <p className="text-slate-600 mb-6">{t("loginSubtitle")}</p>
        <Button onClick={() => setLoginOpen(true)} data-testid="orders-login-btn" className="rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white h-12 px-6">
          {t("login")}
        </Button>
        <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="orders-page">
      <h1 className={`font-heading font-bold text-3xl text-[#1E293B] mb-6 ${lang === "hi" ? "font-devanagari" : ""}`}>
        {t("myOrders")}
      </h1>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 bg-white border border-[#E2E8F0] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E2E8F0] rounded-xl" data-testid="no-orders">
          <Package className="w-14 h-14 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 mb-4">{t("noOrders")}</p>
          <Link to="/products">
            <Button className="rounded-full bg-[#2D5A27] text-white">
              {t("startShopping")} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const Icon = statusIcons[o.status] || Clock;
            return (
              <div
                key={o.id}
                className="bg-white border border-[#E2E8F0] rounded-xl p-4 sm:p-5"
                data-testid={`order-item-${o.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-widest">{t("orderNumber")}</p>
                    <p className="font-mono font-semibold text-[#1E293B]" data-testid={`order-number-${o.id}`}>{o.order_number}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(o.created_at).toLocaleString(lang === "hi" ? "hi-IN" : "en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <Badge variant="outline" className={`${statusColor[o.status]} font-semibold`}>
                    <Icon className="w-3.5 h-3.5 mr-1" /> {t(o.status)}
                  </Badge>
                </div>
                <div className="border-t border-[#E2E8F0] pt-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm text-slate-600">
                    {o.items.length} {t("items")}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{t("total")}</p>
                    <p className="font-heading font-bold text-lg text-[#2D5A27]">₹{o.total.toFixed(0)}</p>
                  </div>
                </div>
                <div className="border-t border-[#E2E8F0] pt-3 mt-3 flex flex-wrap gap-2">
                  <Button
                    onClick={() => handleReorder(o)}
                    data-testid={`reorder-btn-${o.id}`}
                    size="sm"
                    className="rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white h-9 px-4 font-semibold pressable"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> {t("reorder")}
                  </Button>
                  <Link to={`/invoice/${o.id}`} data-testid={`invoice-link-${o.id}`}>
                    <Button variant="outline" size="sm" className="rounded-full border-[#E2E8F0] h-9 px-4 font-semibold">
                      <FileText className="w-3.5 h-3.5 mr-1.5" /> {t("invoice")}
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
